'use client';

import React, { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useSnapshot } from 'valtio';
import { pageLoadingStore, resetPageLoading } from '@/stores/pageLoadingStore';

/**
 * PageLoader handles first-visit FOUC prevention only.
 *
 * The body is hidden *before first paint* by the blocking inline script in
 * `src/app/(frontend)/layout.tsx`, which adds `first-visit` to <html>, plus
 * the matching `html.first-visit body` rule in `animation.css`. This
 * component never hides anything itself — doing that from a useEffect runs
 * after the browser has already painted, which produced the
 * paint → blank → fade "double load" flash.
 *
 * So the contract is one-directional: the script hides, PageLoader reveals.
 * If the class isn't on <html>, there is nothing to reveal and the page just
 * renders normally (repeat visits, non-homepage routes, SPA navigations).
 *
 * Subsequent navigations are handled by PageTransition, which fades page
 * content on route changes.
 */
// Maximum time the body can remain hidden on first visit. Belts-and-suspenders
// safety net in case a loading gate never resolves — without this, the body
// would stay at opacity 0.01 forever.
const PAGELOADER_MAX_HIDE_MS = 2000;

// Duration of the reveal fade once the gates pass.
const REVEAL_MS = 300;

const FIRST_VISIT_CLASS = 'first-visit';

const PageLoader = () => {
  const pathname = usePathname();
  const snap = useSnapshot(pageLoadingStore);
  const isGatedRef = useRef<boolean>(false);
  const revealedRef = useRef<boolean>(false);

  const reveal = () => {
    if (revealedRef.current) return;
    revealedRef.current = true;

    const body = document.body;

    // Pin the pre-paint opacity inline before dropping the class, so removing
    // the `!important` rule can't snap the body to 1 with no transition.
    body.style.opacity = '0.01';
    body.style.transition = 'none';
    document.documentElement.classList.remove(FIRST_VISIT_CLASS);

    // Flush styles so the browser has 0.01 as the transition start value.
    void body.offsetHeight;

    body.style.transition = `opacity ${REVEAL_MS}ms cubic-bezier(0, 0.5, 0.5, 1)`;
    body.style.opacity = '1';

    try { sessionStorage.setItem('hasVisited', '1'); } catch {}
  };

  // Restart the loading gates on entry to the homepage, and arm the safety
  // timer only when the pre-paint script actually gated this document load.
  useEffect(() => {
    if (pathname !== '/') return;

    isGatedRef.current =
      document.documentElement.classList.contains(FIRST_VISIT_CLASS);

    // Kick off the loading gate timer (drives the hero stagger on every
    // entry to the homepage, gated or not).
    resetPageLoading();

    if (!isGatedRef.current) return;
    const safety = window.setTimeout(reveal, PAGELOADER_MAX_HIDE_MS);
    return () => window.clearTimeout(safety);
  }, [pathname]);

  // Reveal the body once the loading gates pass.
  useEffect(() => {
    if (!isGatedRef.current) return;
    if (snap.isPageReady) reveal();
  }, [snap.isPageReady]);

  return null;
};

export default PageLoader;
